// Store credit?
import React, { useContext, useState } from 'react';

// Components
import SectionHeader from '../../../_molecules/SectionHeader';
import LoadingArea from '@molecules/LoadingArea';
import {
    BodyLarge,
    BodyMedium,
    BodySmall,
    DividerLightGreyStyled,
    ErrorText,
    H3,
    H4,
    LabelLarge,
    LinkSmall
} from '@styled';

// Functionality
import { BillingContext } from './BillingProvider';
import useViewer from '@hooks/useViewer';
import useDocumentTitle from '@hooks/useDocumentTitle';
import SectionBreadcrumbs from "../../../_molecules/SectionBreadcrumbs";
import SectionBreadcrumb from "../../../_molecules/SectionBreadcrumb";

const BillingAutoRenewLayout = () => {
    const {
        // Data
        autoRenewErrorMessage,
        autoRenewInfoMessage,
        member,
        memberId,
        savedCards,

        // Methods
        setAutoRenewErrorMessage,
        setAutoRenewInfoMessage,
        setGeneralErrorMessage,
        setGeneralInfoMessage,
        setMember,
        updateMember,
    } = useContext(BillingContext);

    const { viewer } = useViewer();
    const { documentTitle } = useDocumentTitle('Billing & Payments');

    const isUserIneligibleForAutoRenew = () => {
        if (!member) {
            return true;
        }

        const { status, student, special, prospect } = member;
        return status.toUpperCase() !== 'ACTIVE' || student || special || prospect;
    };

    const setMemberAutoRenew = async () => {
        const autoRenew = !!member.auto_renew;
        clearMessages();

        if (!autoRenew && savedCards.length === 0) {
            setAutoRenewErrorMessage('You must have a credit card saved before opting to auto-renew.');
            return;
        }

        if (!autoRenew && !savedCards.find(savedCard => savedCard.default)) {
            setAutoRenewErrorMessage('You must designate a default payment method before opting to auto-renew.');
            return;
        }

        try {
            await updateMember(memberId, {
                auto_renew: !autoRenew,
            });

            setMember({
                ...member,
                auto_renew: !autoRenew,
            });

            setAutoRenewInfoMessage('Auto-renew status updated successfully.');
        } catch (error) {
            setAutoRenewErrorMessage(error.message);
        }
    };

    const clearMessages = () => {
        setGeneralInfoMessage(null);
        setGeneralErrorMessage(null);
        setAutoRenewInfoMessage(null);
        setAutoRenewErrorMessage(null);
    };

    if (!viewer) {
        return <LoadingArea />;
    }

    return (
        <div id="my-icsc-billing">
            <SectionHeader title="My Account" />

            <div className="flex justify-content-between mt-2">
                <SectionBreadcrumbs>
                    <SectionBreadcrumb route="/my-icsc/account">My Account</SectionBreadcrumb>
                    <SectionBreadcrumb route="/my-icsc/account/edit-billing">Billing & Payments</SectionBreadcrumb>
                    <SectionBreadcrumb isActive>Auto-Renew Membership</SectionBreadcrumb>
                </SectionBreadcrumbs>
            </div>

            <H3 className="mt-3 mb-4">Auto-Renew Membership</H3>

            <H4 className="mt-2rem mb-1rem">Adjust Auto-Renew Preferences</H4>

            {viewer.hasSubscription && (
                <>
                    <DividerLightGreyStyled className="mt-2rem mb-2rem"/>

                    <div className="auto-renew-membership-section">
                        <H4 className="mb-0.5rem">Auto-Renew Membership</H4>

                        {member && member.auto_renew && (
                            <div>
                                <BodySmall className="mb-1rem">
                                    You are currently enrolled in Auto-Renew. We will notify you by email before your
                                    membership is set to expire. Your membership won't be processed until 15 days prior
                                    to your paid through date.
                                </BodySmall>

                                <button
                                    type="button"
                                    className="autorenewoptout btn btn-primary"
                                    onClick={() => {
                                        if (confirm('Are you sure you want to opt out of auto-renew?')) {
                                            setMemberAutoRenew();
                                        }
                                    }}
                                >
                                    Opt Out of Auto-Renew
                                </button>
                            </div>
                        )}

                        {member && !member.auto_renew && (
                            <div>
                                <BodySmall className="mb-1rem">
                                    You are not currently enrolled in Auto-Renew. When enrolled in auto-renew, we will
                                    notify you by email before your membership is set to expire. Your membership won't
                                    be processed until 15 days prior to your paid through date.
                                </BodySmall>

                                <button
                                    type="button"
                                    disabled={isUserIneligibleForAutoRenew()}
                                    className="autorenewoptin btn btn-primary"
                                    onClick={() => {
                                        setMemberAutoRenew();
                                    }}
                                >
                                    Enroll in Auto-Renew
                                </button>
                            </div>
                        )}

                        {autoRenewInfoMessage && <BodyMedium className="mt-1rem">{autoRenewInfoMessage}</BodyMedium>}
                        {autoRenewErrorMessage && (
                            <BodyMedium className="mt-1rem">
                                <ErrorText>{autoRenewErrorMessage}</ErrorText>
                            </BodyMedium>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default BillingAutoRenewLayout;
